import { Box } from "@repo/components/src/ui/box";
import { VStack } from "@repo/components/src/ui/vstack";
import { CurrencyCode } from "@repo/components/src/utils/money/currencies";
import { ProductsSelectionQuery } from "@repo/graphql/documents/product/query.generated";
import { Hourglass, Shield } from "lucide-react-native";
import React from "react";
import PaymentsCard from "./PaymentsCard";
import SectionHeader from "./SectionHeader";
import type { SectionStatus } from "./sectionStatus";
import TermRow from "./TermRow";
import { rightsOfUseValue } from "./termsHelpers";
import type { DraftPreviewModel } from "../draftPreviewTypes";

type ProductNode =
  ProductsSelectionQuery["product"]["list"]["edges"][number]["node"];

type Props = {
  model: DraftPreviewModel;
  products: Map<string, ProductNode>;
  status: SectionStatus;
  currencyCode: CurrencyCode;
};

/**
 * "Términos" — the mobile app's `TermsCard` (derecho de uso / tiempo en perfil)
 * and `PaymentsCard` (pagos y canjes) folded into one section, as the brief asks:
 * one status for the two together rather than the wizard's separate steps 4/5.
 */
const TermsSection = React.memo(
  ({ model, products, status, currencyCode }: Props) => {
    const rightOfUseLabel = rightsOfUseValue(model.rightsOfUse);
    const timeInProfileLabel = rightsOfUseValue(model.postInProfileTime);

    return (
      <>
        <SectionHeader title="Términos" status={status} />
        <VStack space="md">
          {rightOfUseLabel || timeInProfileLabel ? (
            <Box className="rounded-3xl bg-white overflow-hidden border border-outline-100">
              {rightOfUseLabel ? (
                <TermRow
                  icon={Shield}
                  label="Derecho de uso"
                  value={rightOfUseLabel}
                  isLast={!timeInProfileLabel}
                />
              ) : null}
              {timeInProfileLabel ? (
                <TermRow
                  icon={Hourglass}
                  label="Tiempo en perfil"
                  value={timeInProfileLabel}
                  isLast
                />
              ) : null}
            </Box>
          ) : null}

          <PaymentsCard
            budgetType={model.budgetType}
            fixedBudget={model.fixedBudget}
            priceProduct={model.priceProduct}
            priceProductIds={model.priceProductIds}
            giftCardPrice={model.giftCardPrice}
            products={products}
            currencyCode={currencyCode}
          />
        </VStack>
      </>
    );
  },
);

TermsSection.displayName = "TermsSection";

export default TermsSection;
